import { IProduct } from "@/services/mocks/productData";
// import { uploadFile } from "@/services/fileService";
import { apiClient } from "../core/apiClient";
import { default as categoryService, type ICategory } from "../products/categoryService";

/**
 * Interfaz para la respuesta de productos desde el frontend
 */
export interface IProductResponse {
  products: IProduct[];
}

/**
 * Interfaz para estadísticas de productos (a futuro)
 */
export interface IProductStats {
  totalProducts: number;
  availableProducts: number;
  unavailableProducts: number;
  categories: {
    [key: string]: number;
  };
}

export interface ICreateProductData {
  nombre: string;
  descripcion: string;
  precio: number;
  categoria: string; // Nombre legible de la categoría
  imagen?: string;
  disponible: boolean;
  type: 'single' | 'pack';
  pack_size: number | null;
  imageFile?: File; // Archivo de imagen para subir
  posIds?: number[];
}

export interface IUpdateProductData {
  nombre: string;
  descripcion: string;
  precio: number;
  categoria: string; // Nombre legible de la categoría
  imagen?: string;
  disponible: boolean;
  type: 'single' | 'pack';
  pack_size: number | null;
  imageFile?: File; // Archivo de imagen para subir
  posIds?: number[];
}

/**
 * Estructura que devuelve el backend para un producto
 */
interface IBackendProduct {
  id: string;
  nombre: string;
  descripcion: string;
  precio: number;
  categoryId: number;
  imagen_url?: string;
  esta_disponible: boolean;
  type?: 'single' | 'pack';
  pack_size?: number | null;
  // Cuando pedimos relaciones, el backend incluirá la categoría
  category?: ICategory;
  posDisponibles?: Array<{ id: number; nombre: string }>;
}

/**
 * Servicio para manejo de productos contra la API real
 */
class ProductService {
  /**
   * Convierte un producto proveniente del backend al tipo utilizado en el frontend
   */
  private static mapBackendProduct(product: IBackendProduct): IProduct {
    return {
      id: product.id,
      nombre: product.nombre,
      descripcion: product.descripcion,
      precio: Number(product.precio),
      categoria: product.category?.nombre || String(product.categoryId),
      imagen: product.imagen_url,
      disponible: product.esta_disponible,
      stock: 0, // Valor por defecto, el backend aún no lo maneja
      fechaCreacion: new Date().toISOString(), // Placeholder
      type: product.type || 'single',
      pack_size: product.pack_size ?? null,
      posIds: Array.isArray(product.posDisponibles)
        ? product.posDisponibles.map((p) => p.id)
        : undefined,
    };
  }

  /**
   * Obtiene todos los productos
   */
  static async getAllProducts(): Promise<IProductResponse> {
    const response = await apiClient.get<IBackendProduct[]>("/api/v1/products");
    const products = response.data.map(this.mapBackendProduct);
    // console.log(products);
    return { products };
  }

  /**
   * Crea un nuevo producto
   */
  static async createProduct(data: ICreateProductData): Promise<IProduct> {

    

    // 1. Si el usuario seleccionó categoría existente/creó una nueva:
    const category = await categoryService.findOrCreateByName(data.categoria);



    // 2. Construimos FormData
    const formData = new FormData();
    formData.append("categoryId", String(category.id));
    formData.append("nombre", data.nombre);
    formData.append("descripcion", data.descripcion);
    formData.append("precio", String(data.precio));
    formData.append("disponible", String(data.disponible));
    formData.append("type", data.type);
    if (data.type === 'pack' && data.pack_size !== null) {
      formData.append("pack_size", String(data.pack_size));
    }
    if (Array.isArray(data.posIds)) {
      for (const id of data.posIds) {
        formData.append('posIds[]', String(id));
      }
    }
    if (data.imageFile) {
      formData.append("imagen", data.imageFile);
    } else if (data.imagen) {
      formData.append("imagen_url", data.imagen);   // URL externa opcional
    }

    // 3. Petición al backend
    const response = await apiClient.post<IBackendProduct>(
      "/api/v1/products",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return this.mapBackendProduct(response.data);
  }

  /**
   * Actualiza un producto existente
   */
  static async updateProduct(
    productId: string,
    updatedData: IUpdateProductData
  ): Promise<IProduct> {
    // Subir nueva imagen si corresponde
    // let imagePath
    // if (updatedData.imageFile) {
    //   const uploadResult = await uploadFile(updatedData.imageFile, "products");
    //   if (!uploadResult.success || !uploadResult.filePath) {
    //     throw new Error(uploadResult.error || "Error al subir la imagen");
    //   }
    //   imagePath = uploadResult.filePath;
    // }

    // Obtener/crear categoría si cambió
    const category = await categoryService.findOrCreateByName(
      updatedData.categoria
    );
    const formData = new FormData();
    formData.append("categoryId", String(category.id));
    formData.append("nombre", updatedData.nombre);
    formData.append("descripcion", updatedData.descripcion);
    formData.append("precio", String(updatedData.precio));
    formData.append("disponible", String(updatedData.disponible));
    formData.append("type", updatedData.type);
    if (updatedData.type === 'pack' && updatedData.pack_size !== null) {
      formData.append("pack_size", String(updatedData.pack_size));
    }
    if (Array.isArray(updatedData.posIds)) {
      for (const id of updatedData.posIds) {
        formData.append('posIds[]', String(id));
      }
    }
    if (updatedData.imageFile) {
      formData.append("imagen", updatedData.imageFile);
    } else {
      // Always send imagen_url: if empty string, backend will clear it
      formData.append("imagen_url", updatedData.imagen ?? "");
    }

    const response = await apiClient.put<IBackendProduct>(
      `/api/v1/products/${productId}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return this.mapBackendProduct(response.data);
  }

  /**
   * Elimina un producto
   */
  static async deleteProduct(productId: string): Promise<void> {
    await apiClient.delete(`/api/v1/products/${productId}`);
  }
}

export default ProductService;
